import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import BrandPanel from "../components/BrandPanel";
import LangToggle from "../components/LangToggle";
import { useLang } from "../context/LangContext";
import { api, formatApiError } from "../lib/api";
import { ScrollText, Building2, KeyRound, Lock, ArrowRight, Loader2 } from "lucide-react";
import PhoneField from "../components/PhoneField";
import { toast } from "sonner";

export default function ForgotPassword() {
    const navigate = useNavigate();
    const { lang, t, toggle } = useLang();

    // stages: account → otp → reset
    const [stage, setStage] = useState("account");
    const [companyName, setCompanyName] = useState("");
    const [phone, setPhone] = useState("");
    const [otp, setOtp] = useState("");
    const [resetToken, setResetToken] = useState("");
    const [newPw, setNewPw] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [resendIn, setResendIn] = useState(0);

    // Tick down the resend cooldown.
    useEffect(() => {
        if (resendIn <= 0) return;
        const id = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(id);
    }, [resendIn]);

    const startOtp = async (e, isResend = false) => {
        e?.preventDefault?.();
        setError("");
        setBusy(true);
        try {
            const url = isResend ? "/auth/forgot/resend" : "/auth/forgot/start";
            await api.post(url, { company_name: companyName, phone });
            setStage("otp");
            setResendIn(30);
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || err.message;
            setError(msg);
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    const verifyOtp = async (e) => {
        e?.preventDefault?.();
        setError("");
        setBusy(true);
        try {
            const { data } = await api.post("/auth/forgot/verify", {
                company_name: companyName,
                phone,
                otp,
            });
            setResetToken(data.reset_token);
            setStage("reset");
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || err.message;
            setError(msg);
        } finally {
            setBusy(false);
        }
    };

    const resetPw = async (e) => {
        e?.preventDefault?.();
        setError("");
        if (newPw !== confirmPw) {
            setError(t.passwordsDontMatch);
            return;
        }
        setBusy(true);
        try {
            await api.post("/auth/forgot/reset", {
                reset_token: resetToken,
                new_password: newPw,
            });
            toast.success(t.forgotDoneToast);
            navigate("/login", { replace: true });
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || err.message;
            setError(msg);
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    const iconSide = lang === "ar" ? { right: "0.75rem" } : { left: "0.75rem" };
    const inputPad = lang === "ar" ? "h-11 pe-10 ps-3" : "h-11 ps-10 pe-3";

    return (
        <div className="relative min-h-screen w-full app-bg" dir={t.dir}>
            <LangToggle lang={lang} onToggle={toggle} t={t} />

            <div className="mx-auto grid min-h-screen max-w-7xl grid-cols-1 lg:grid-cols-2">
                <div className="relative hidden p-6 lg:block">
                    <BrandPanel lang={lang} strings={t} />
                </div>

                <div className="flex items-center justify-center px-4 py-8 sm:px-6 sm:py-12 lg:px-12">
                    <div className="w-full max-w-md fade-up">
                        <div className="mb-6 flex items-center gap-3 sm:mb-8">
                            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-neutral-900 text-white sm:h-12 sm:w-12">
                                <ScrollText className="h-5 w-5 sm:h-6 sm:w-6" />
                            </div>
                            <div>
                                <h1 className="font-heading text-xl font-bold sm:text-2xl">{t.brand}</h1>
                                <p className="text-xs text-neutral-500 sm:text-sm">{t.forgotTitle}</p>
                            </div>
                        </div>

                        {stage === "account" ? (
                            <>
                                <h2 className="font-heading text-2xl font-bold sm:text-3xl">{t.forgotTitle}</h2>
                                <p className="mt-2 text-sm text-neutral-500">{t.forgotPhoneStep}</p>
                                <p className="mt-1 text-xs text-neutral-500">{t.forgotPhoneHint}</p>

                                <form onSubmit={startOtp} className="mt-6 space-y-4 sm:mt-8" data-testid="forgot-account-form">
                                    <div>
                                        <Label className="mb-1.5 block text-sm font-medium">{t.companyLabel}</Label>
                                        <div className="relative">
                                            <Building2 className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" style={iconSide} />
                                            <Input
                                                value={companyName}
                                                onChange={(e) => setCompanyName(e.target.value)}
                                                placeholder={t.companyPlaceholder}
                                                className={inputPad}
                                                autoFocus
                                                required
                                                data-testid="forgot-account-input"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <Label className="mb-1.5 block text-sm font-medium">{t.phoneLabel}</Label>
                                        <PhoneField
                                            value={phone}
                                            onChange={setPhone}
                                            testId="forgot-phone"
                                        />
                                    </div>

                                    {error ? (
                                        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="forgot-error">
                                            {error}
                                        </div>
                                    ) : null}

                                    <Button
                                        type="submit"
                                        disabled={busy || resendIn > 0}
                                        className="h-11 w-full bg-neutral-900 text-white hover:bg-neutral-800 disabled:bg-neutral-200 disabled:text-neutral-500"
                                        data-testid="forgot-send-otp-btn"
                                    >
                                        {busy ? <Loader2 className="ms-1 h-4 w-4 animate-spin" /> : null}
                                        {busy
                                            ? t.otpSending
                                            : resendIn > 0
                                                ? t.forgotResendIn.replace("{s}", resendIn)
                                                : t.forgotSendOtp}
                                    </Button>
                                </form>
                            </>
                        ) : null}

                        {stage === "otp" ? (
                            <>
                                <h2 className="font-heading text-2xl font-bold sm:text-3xl">{t.forgotOtpStep}</h2>
                                <p className="mt-2 text-sm text-neutral-500">{t.forgotOtpHint}</p>

                                <form onSubmit={verifyOtp} className="mt-6 space-y-4 sm:mt-8" data-testid="forgot-otp-form">
                                    <div>
                                        <Label className="mb-1.5 block text-sm font-medium">{t.forgotOtpLabel}</Label>
                                        <div className="relative">
                                            <KeyRound className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" style={iconSide} />
                                            <Input
                                                inputMode="numeric"
                                                maxLength={6}
                                                value={otp}
                                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                                                placeholder="••••••"
                                                className={`${inputPad} text-center font-numbers text-lg tracking-[0.4em]`}
                                                autoFocus
                                                required
                                                data-testid="forgot-otp-input"
                                            />
                                        </div>
                                    </div>

                                    {error ? (
                                        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="forgot-error">
                                            {error}
                                        </div>
                                    ) : null}

                                    <Button
                                        type="submit"
                                        disabled={busy}
                                        className="h-11 w-full bg-neutral-900 text-white hover:bg-neutral-800"
                                        data-testid="forgot-verify-btn"
                                    >
                                        {busy ? <Loader2 className="ms-1 h-4 w-4 animate-spin" /> : null}
                                        {t.forgotVerify}
                                    </Button>

                                    <button
                                        type="button"
                                        disabled={busy || resendIn > 0}
                                        onClick={(e) => startOtp(e, true)}
                                        className="block w-full text-center text-sm font-semibold text-amber-700 hover:underline disabled:cursor-not-allowed disabled:text-neutral-400 disabled:no-underline"
                                        data-testid="forgot-resend-btn"
                                    >
                                        {busy
                                            ? t.otpSending
                                            : resendIn > 0
                                                ? t.forgotResendIn.replace("{s}", resendIn)
                                                : t.forgotResend}
                                    </button>
                                </form>
                            </>
                        ) : null}

                        {stage === "reset" ? (
                            <>
                                <h2 className="font-heading text-2xl font-bold sm:text-3xl">{t.forgotResetStep}</h2>
                                <p className="mt-2 text-sm text-neutral-500">{t.forgotResetHint}</p>

                                <form onSubmit={resetPw} className="mt-6 space-y-4 sm:mt-8" data-testid="forgot-reset-form">
                                    <div>
                                        <Label className="mb-1.5 block text-sm font-medium">{t.newPassword}</Label>
                                        <div className="relative">
                                            <Lock className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" style={iconSide} />
                                            <Input
                                                type="password"
                                                value={newPw}
                                                onChange={(e) => setNewPw(e.target.value)}
                                                className={inputPad}
                                                autoComplete="new-password"
                                                required
                                                data-testid="forgot-new-password"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="mb-1.5 block text-sm font-medium">{t.confirmNewPassword}</Label>
                                        <Input
                                            type="password"
                                            value={confirmPw}
                                            onChange={(e) => setConfirmPw(e.target.value)}
                                            className={lang === "ar" ? "h-11 ps-3" : "h-11 pe-3"}
                                            autoComplete="new-password"
                                            required
                                            data-testid="forgot-confirm-password"
                                        />
                                    </div>

                                    {error ? (
                                        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="forgot-error">
                                            {error}
                                        </div>
                                    ) : null}

                                    <Button
                                        type="submit"
                                        disabled={busy}
                                        className="h-11 w-full bg-neutral-900 text-white hover:bg-neutral-800"
                                        data-testid="forgot-reset-btn"
                                    >
                                        {busy ? <Loader2 className="ms-1 h-4 w-4 animate-spin" /> : null}
                                        {t.forgotResetSubmit}
                                    </Button>
                                </form>
                            </>
                        ) : null}

                        <p className="mt-6 text-center text-sm text-neutral-600">
                            <Link to="/login" className="inline-flex items-center gap-1 font-semibold text-amber-700 hover:underline" data-testid="forgot-back-to-login">
                                <ArrowRight className={lang === "ar" ? "h-4 w-4" : "h-4 w-4 rotate-180"} />
                                {t.forgotBackToLogin}
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
